/**
 * Резервная форма критериев (FE-072, MVP_PRD §24).
 *
 * Форма пишет те же поля Criteria Document, что и консультант, через
 * `PATCH /criteria`: пути берутся из того же allowlist, значения проходят ту же
 * валидацию (MVP_TECH_RFC §9). Отличие одно — источником является не речь
 * пользователя, а элементы формы, поэтому **акценты и допуск бюджета здесь
 * задать нельзя**: они существуют только вместе с цитатой (MATCHING §4.2).
 *
 * Порядок операций значим. `drivetrain.value` при незаданном режиме
 * выставляет `required`, поэтому «желателен» дописывается следующей операцией.
 */

import { isUnlimitedBudget } from '@entities/search-session';
import type {
  BodyType,
  CriteriaPatchOperation,
  CriteriaPayload,
  FuelType,
  PriorityCode,
  UsageScenario,
} from '@entities/search-session';

export type BodyTypeChoice = BodyType | 'any';
/** Год строкой либо явное «без ограничения». */
export type MaxAgeChoice = string;

/** Базовые варианты списка возраста; фактический год добавляется к ним. */
export const MAX_AGE_BASE_YEARS: readonly number[] = [3, 5, 6, 8, 10];
export const MAX_AGE_NO_LIMIT = 'none';
export type DrivetrainChoice = 'any' | 'awd_required' | 'awd_preferred' | 'fwd' | 'rwd';
export type TransmissionChoice = 'any' | 'automatic' | 'manual';

export interface FallbackFormValues {
  budgetEuros: string;
  budgetMode: 'vehicle_only' | 'total';
  reserveEuros: string;
  bodyType: BodyTypeChoice;
  maxAge: MaxAgeChoice;
  annualMileageKm: string;
  usage: UsageScenario;
  fuels: FuelType[];
  drivetrain: DrivetrainChoice;
  transmission: TransmissionChoice;
  priority: PriorityCode;
}

/** Число из поля формы. Пустая строка и мусор — это «не задано», а не ноль. */
export function parseAmount(value: string): number | null {
  const normalized = value.replace(/[\s.,']/g, '');
  if (normalized === '') return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : null;
}

/** Операции patch по значениям формы. Чистая функция — проверяется без DOM. */
export function fallbackOperations(values: FallbackFormValues): CriteriaPatchOperation[] {
  const operations: CriteriaPatchOperation[] = [];

  const budget = parseAmount(values.budgetEuros);
  if (budget !== null && budget > 0) {
    operations.push({ path: 'budget.total_cents', value: budget * 100 });
    operations.push({ path: 'budget.mode', value: values.budgetMode });
    if (values.budgetMode === 'total') {
      // Резерв существует только у общего бюджета; «не учитывать» — это ноль,
      // и вопрос о резерве закрывается тоже (MVP_PRD §10).
      operations.push({
        path: 'budget.reserve_cents',
        value: (parseAmount(values.reserveEuros) ?? 0) * 100,
      });
    }
  }

  if (values.bodyType === 'any') {
    operations.push({ path: 'body_types.mode', value: 'any' });
  } else {
    operations.push({ path: 'body_types.values', value: [values.bodyType] });
  }

  operations.push({
    path: 'max_age.years',
    // `null` на этом пути — явное «без ограничения» (см. patch engine).
    value: values.maxAge === MAX_AGE_NO_LIMIT ? null : Number(values.maxAge),
  });

  const mileage = parseAmount(values.annualMileageKm);
  if (mileage !== null && mileage > 0) {
    operations.push({ path: 'annual_mileage_km', value: mileage });
  }

  operations.push({ path: 'usage', value: [values.usage] });

  if (values.fuels.length > 0) {
    operations.push({ path: 'fuel.allowed', value: values.fuels });
  }

  switch (values.drivetrain) {
    case 'any':
      operations.push({ path: 'drivetrain.mode', value: 'any' });
      break;
    case 'awd_required':
      operations.push({ path: 'drivetrain.value', value: 'awd' });
      break;
    case 'awd_preferred':
      operations.push({ path: 'drivetrain.value', value: 'awd' });
      operations.push({ path: 'drivetrain.mode', value: 'preferred' });
      break;
    default:
      operations.push({ path: 'drivetrain.value', value: values.drivetrain });
      break;
  }

  if (values.transmission === 'any') {
    operations.push({ path: 'transmission.mode', value: 'any' });
  } else {
    operations.push({ path: 'transmission.value', value: values.transmission });
  }

  operations.push({ path: 'priorities', value: [values.priority] });

  return operations;
}

/** Варианты списка возраста: базовые плюс уже названный пользователем год. */
export function maxAgeOptions(current: MaxAgeChoice): MaxAgeChoice[] {
  const years = new Set(MAX_AGE_BASE_YEARS);
  const parsed = Number(current);
  if (Number.isInteger(parsed) && parsed > 0) years.add(parsed);
  return [...[...years].sort((left, right) => left - right).map(String), MAX_AGE_NO_LIMIT];
}

/** Значения формы по текущим критериям: заполненное консультантом не теряется. */
export function fallbackDefaults(payload: CriteriaPayload): FallbackFormValues {
  const listing = payload.budget.listing_budget_cents;
  const years = payload.max_age.years;

  return {
    // «Не ограничен» в поле суммы показывать нечем — оно остаётся пустым.
    budgetEuros:
      listing === null || isUnlimitedBudget(listing) ? '' : String(Math.round(listing / 100)),
    budgetMode: payload.budget.mode ?? 'vehicle_only',
    reserveEuros:
      payload.budget.reserve_cents > 0
        ? String(Math.round(payload.budget.reserve_cents / 100))
        : '',
    bodyType: payload.body_types.values[0] ?? (payload.body_types.mode === 'any' ? 'any' : 'suv'),
    // Уже названный возраст сохраняется как есть: подстановка ближайшего
    // варианта списка молча ужесточила бы критерий пользователя.
    maxAge: years !== null ? String(years) : payload.max_age.no_limit ? MAX_AGE_NO_LIMIT : '6',
    annualMileageKm: payload.annual_mileage_km === null ? '' : String(payload.annual_mileage_km),
    usage: payload.usage[0] ?? 'mixed',
    fuels: payload.fuel.allowed.length > 0 ? payload.fuel.allowed : ['petrol', 'diesel'],
    drivetrain: drivetrainChoice(payload),
    transmission: payload.transmission.value ?? 'any',
    priority: payload.priorities[0]?.code ?? 'reliability',
  };
}

function drivetrainChoice(payload: CriteriaPayload): DrivetrainChoice {
  const { value, mode } = payload.drivetrain;
  if (value === 'awd') return mode === 'preferred' ? 'awd_preferred' : 'awd_required';
  if (value === 'fwd' || value === 'rwd') return value;
  return 'any';
}
